import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { US } from 'country-flag-icons/react/3x2';
import { MX } from 'country-flag-icons/react/3x2';
import { ReactComponent as KairosLogo } from '../imgs/kairos_white.svg';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.home, href: '/#home' },
    { name: t.nav.howWeWork, href: '/#diagnose' },
    { name: t.nav.process, href: '/#process' },
    { name: t.nav.calculator, href: '/#calculator' },
    { name: t.nav.getStarted, href: '/#get-started' },
    { name: t.nav.aboutUs, href: '/about' }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();

    // Close mobile menu if open
    setIsMobileMenuOpen(false);

    // Check if it's a hash link
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2); // Remove '/#'

      // If we're not on the home page, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation, then scroll
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      // Regular navigation (e.g., to /about)
      navigate(href);
      // Scroll to top after navigation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const languages = [
    { code: 'en' as const, FlagComponent: US, label: 'EN' },
    { code: 'es' as const, FlagComponent: MX, label: 'ES' }
  ];

  return (
    <>
      {/* Black fade overlay */}
      {isScrolled && !isMobileMenuOpen && (
        <div
          className="fixed top-0 left-0 right-0 h-32 pointer-events-none z-40 transition-opacity duration-300"
          style={{
            background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.8) 0%, rgba(15, 23, 42, 0) 100%)'
          }}
        />
      )}

      <nav
        className={`fixed z-50 transition-all duration-300 ${
          isMobileMenuOpen
            ? 'top-0 left-0 right-0'
            : isScrolled
              ? 'top-6 left-6 right-6'
              : 'top-0 left-0 right-0'
        }`}
      >
      <div
        className={`transition-all duration-100 ${
          isMobileMenuOpen
            ? 'px-6 py-6'
            : isScrolled
              ? 'rounded-full px-8 py-4 mx-auto max-w-6xl'
              : 'px-6 py-6'
        }`}
        style={{
          background: isScrolled || isMobileMenuOpen
            ? 'rgba(255, 255, 255, 0.15)'
            : 'transparent',
          backdropFilter: isScrolled || isMobileMenuOpen ? 'blur(2px) saturate(180%)' : 'none',
          WebkitBackdropFilter: isScrolled || isMobileMenuOpen ? 'blur(2px) saturate(180%)' : 'none',
          border: isScrolled || isMobileMenuOpen ? '1px solid rgba(255, 255, 255, 0.8)' : 'none',
          borderTop: isMobileMenuOpen ? 'none' : undefined,
          borderLeft: isMobileMenuOpen ? 'none' : undefined,
          borderRight: isMobileMenuOpen ? 'none' : undefined,
          borderRadius: isMobileMenuOpen ? '0 0 2rem 2rem' : undefined,
          boxShadow: isScrolled || isMobileMenuOpen
            ? '0 8px 32px rgba(31, 38, 135, 0.2), inset 0 4px 20px rgba(255, 255, 255, 0.3)'
            : 'none'
        }}
      >
        <div className={isScrolled ? '' : 'max-w-7xl mx-auto'}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/#home" onClick={(e) => handleNavClick(e, '/#home')} className="flex items-center gap-3">
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="h-8 overflow-hidden"
            >
              <KairosLogo
                className="h-full w-auto"
                style={{
                  fill: 'url(#logo-gradient)',
                  transform: 'scale(1.5)',
                  transformOrigin: 'center'
                }}
              />
              <svg width="0" height="0">
                <defs>
                  <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: 'var(--color-accent)', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            <span className="text-2xl font-bold text-white">Kairos</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <React.Fragment key={link.name}>
                {link.name === t.nav.aboutUs && (
                  <span className="text-gray-400">|</span>
                )}
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-base font-medium transition-colors duration-200 hover:opacity-80"
                  style={{
                    color: isScrolled ? 'var(--color-text-primary)' : 'var(--color-text-primary)'
                  }}
                >
                  {link.name}
                </a>
              </React.Fragment>
            ))}

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/10"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {(() => {
                  const currentLang = languages.find(l => l.code === language);
                  const FlagComponent = currentLang?.FlagComponent;
                  return FlagComponent ? <FlagComponent className="w-5 h-4" /> : null;
                })()}
                <span className="text-sm font-semibold">{languages.find(l => l.code === language)?.label}</span>
              </button>

              {isLangOpen && (
                <div
                  className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden shadow-lg"
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {languages.map((lang) => {
                    const FlagComponent = lang.FlagComponent;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className="flex items-center space-x-3 px-4 py-2 w-full text-left hover:bg-white/10 transition-colors"
                        style={{
                          color: language === lang.code ? 'var(--color-primary)' : 'var(--color-text-primary)'
                        }}
                      >
                        <FlagComponent className="w-5 h-4" />
                        <span className="text-sm font-semibold">{lang.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden md:block">
            <motion.a
              href="/#get-started"
              onClick={(e) => handleNavClick(e, '/#get-started')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 rounded-lg font-semibold text-white shadow-lg transition-shadow duration-300 hover:shadow-xl"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {t.nav.startNow}
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              React.createElement(FaTimes, {
                className: "w-6 h-6",
                style: { color: 'var(--color-primary)' }
              })
            ) : (
              React.createElement(FaBars, {
                className: "w-6 h-6",
                style: { color: 'var(--color-primary)' }
              })
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              className="md:hidden overflow-hidden"
            >
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, delay: 0}}
                className="pt-4 pb-3 space-y-3"
              >
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="block px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                    style={{
                      color: 'var(--color-text-primary)',
                      backgroundColor: 'var(--color-surface)'
                    }}
                  >
                    {link.name}
                  </a>
                ))}

                {/* Mobile Language Selector */}
                <div className="px-4 py-2">
                  <div className="flex space-x-2">
                    {languages.map((lang) => {
                      const FlagComponent = lang.FlagComponent;
                      return (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center space-x-2 px-3 py-2 rounded-lg flex-1 justify-center transition-colors"
                          style={{
                            backgroundColor: language === lang.code ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.1)',
                            color: language === lang.code ? 'white' : 'var(--color-text-primary)'
                          }}
                        >
                          <FlagComponent className="w-5 h-4" />
                          <span className="text-sm font-semibold">{lang.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <a
                  href="/#get-started"
                  onClick={(e) => handleNavClick(e, '/#get-started')}
                  className="block px-4 py-2 rounded-lg font-semibold text-white text-center"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {t.nav.startNow}
                </a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
